import type { Account } from "./account";

export enum StrategyName {
	Session = "session",
	LeastUsed = "least-used",
	SessionAffinity = "session-affinity",
	SessionDrainSoonest = "session-drain-soonest",
	// Same strategy class in "strict" mode: one canonical comparator ranks
	// every available account (earliest weekly reset first; an active 5h
	// session only breaks ties instead of gating position 0).
	SessionDrainSoonestStrict = "session-drain-soonest-strict",
}

/**
 * Interface for strategy-specific database operations
 * Allows strategies to interact with the database without direct SQL access
 */
export interface StrategyStore {
	/**
	 * Reset session for an account
	 * Updates session_start and session_request_count
	 */
	resetAccountSession(accountId: string, timestamp: number): void;

	/**
	 * Get all accounts (optional method for strategies that need full account list)
	 */
	getAllAccounts?(): Account[] | Promise<Account[]>;

	/**
	 * Update account request count
	 */
	updateAccountRequestCount?(accountId: string, count: number): void;

	/**
	 * Get account by ID
	 */
	getAccount?(accountId: string): Account | null | Promise<Account | null>;

	/**
	 * Pause an account
	 */
	pauseAccount?(accountId: string): void;

	/**
	 * Resume a paused account
	 */
	resumeAccount?(accountId: string): void;

	/**
	 * Get the representative utilization (0–100) for an account based on its
	 * most-constrained usage window. Returns null when no usage data is available.
	 */
	getAccountUtilization?(accountId: string, provider: string): number | null;

	/**
	 * Get the epoch-ms reset time of the account's weekly_all (all-models
	 * weekly) usage window — the point at which unused capacity is lost and
	 * fresh capacity becomes available. Returns null when unknown or when the
	 * reset has already passed (stale telemetry that hasn't caught up yet).
	 */
	getAccountWeeklyReset?(accountId: string, provider: string): number | null;
}
